import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { PAGED_LEASING_COMMISSIONS_QUERY } from '../queries';
import { LEASING_COMMISSION_DELETED } from '../subscriptions';
import { Card } from '@totalsoft/rocket-ui';
import LeasingCommissionListContainer from './LeasingCommissionListContainer';
import LeasingCommissionModalComponent from 'features/leasingCommission/edit/LeasingCommissionModalComponent';
import { getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import useSubscription from 'hooks/apollo/useSubscription';

export const leasingCommissionsStore = getListBrowserStore({
  orderByFields: [
    {
      caption: 'LeasingCommission.CommissionName',
      value: 'CommissionName'
    }
  ],
  pager: {
    orderBy: 'CommissionName',
    direction: 'asc'
  }
});

const LeasingCommissionListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_LEASING_COMMISSIONS_QUERY,
        name: 'pagedLeasingCommissions'
      }
    }),
    []
  );

  const columns = useMemo(
    () => [
      { name: 'code', caption: 'LeasingCommission.CommissionCode', width: 8 },
      { name: 'name', caption: 'LeasingCommission.CommissionName', width: 10 },
      { name: 'percent', caption: 'LeasingCommission.CommissionPercent', width: 8 },
      { name: 'value', caption: 'LeasingCommission.CommissionValue', width: 8 },
      { name: 'assignedTo', caption: 'LeasingCommission.AssignedTo', width: 10 },
      { name: 'calculationType', caption: 'LeasingCommission.CalculationType', width: 18 },
      { name: 'readOnly', caption: 'LeasingCommission.ReadOnly', width: 14, align: 'center' },
      { name: 'isSubsidy', caption: 'LeasingCommission.IsSubsidy', width: 10, align: 'center' },
      { name: 'isCommission', caption: 'LeasingCommission.IsCommission', width: 10, align: 'center' },
      { name: 'actions', caption: 'Actions', width: 10, align: 'right' }
    ],
    []
  );

  useSubscription(LEASING_COMMISSION_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('LeasingCommission.Removed'), 'success');
      client.cache.evict({ id: `LeasingCommission:{"commissionId":${data.leasingCommissionDeleted.commissionId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedLeasingCommissions' });
      client.cache.gc();
    }
  });

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser
        name='leasing-commissions-browser'
        store={leasingCommissionsStore}
        ItemComponent={LeasingCommissionListContainer}
        columns={columns}
        layout='fixed'
      />
      <ExportModalComponent store={leasingCommissionsStore} options={fetchingOptions} />
      <LeasingCommissionModalComponent />
      <ApolloFetchingComponent store={leasingCommissionsStore} options={fetchingOptions} />
    </Card>
  );
};

export default LeasingCommissionListBrowser;
